/**
 * Etiquetas legibles para los códigos del catálogo de clasificación.
 *
 * Vive suelto y no en un componente porque lo usan la pantalla y el editor, y
 * tenerlo dos veces era garantía de que se separen: `ERP` salía "Erp" en la
 * tabla y "ERP" en el formulario, o al revés.
 */

/**
 * Siglas del dominio que van SIEMPRE en mayúsculas.
 *
 * Es una lista explícita y no una heurística por longitud: `CONTROL_PRESION_RED`
 * tiene un token de 3 letras que es una palabra común, y "Control presion RED"
 * es peor que el problema que se venía a arreglar. Si el backend agrega un
 * código con una sigla nueva, se muestra como palabra hasta que se agregue acá
 * — visible y sin consecuencias, que es la forma correcta de fallar.
 */
const SIGLAS = new Set(['ERP', 'GNC', 'DMA', 'AT', 'MT', 'BT', 'OPC', 'GLP']);

/**
 * `SERVICIO_GENERAL_P` → `Servicio general P`, `ENTREGA_GNC` → `Entrega GNC`.
 *
 * Los códigos del catálogo son mayúsculas con guiones bajos y mezclan palabras
 * con **siglas** (`ERP`, `GNC`, `DMA`) e **iniciales** (`SERVICIO_GENERAL_P`).
 * Bajarlos enteros a minúscula convierte las siglas en palabras —"Erp", "Gnc"—
 * y eso es una falta de ortografía, no un detalle: son nombres propios del
 * dominio y el operador los lee así en el resto del sistema.
 *
 * El resto va en formato oración (sólo la inicial de la primera palabra), que es
 * como se escriben las etiquetas en la aplicación.
 *
 * No se acentúa: los códigos no traen acentos y adivinarlos produciría textos
 * que no coinciden con lo que valida el backend.
 */
export function etiquetaCodigo(codigo: string): string {
  const tokens = codigo.split('_').filter((t) => t.length);
  let yaHuboPalabra = false;
  return tokens
    .map((token) => {
      // Las iniciales sueltas (`..._P`, `..._G`) son siglas de un carácter.
      if (SIGLAS.has(token) || token.length === 1) return token.toUpperCase();
      const minuscula = token.toLowerCase();
      if (yaHuboPalabra) return minuscula;
      yaHuboPalabra = true;
      return minuscula.charAt(0).toUpperCase() + minuscula.slice(1);
    })
    .join(' ');
}

/** De dónde salió cada campo de la clasificación. Coincide con `OrigenClasificacion`. */
const ETIQUETAS_ORIGEN: Record<string, string> = {
  'tag-opc': 'Tag OPC',
  'declaracion-cliente': 'Declaración del cliente',
  division: 'División',
  'grupo-operador': 'Grupo del operador',
  'patron-nombre': 'Patrón del nombre',
  'presion-medida': 'Presión medida',
  'consumo-medido': 'Consumo medido',
  'geo-osm': 'Cartografía',
  manual: 'Manual',
};

export function etiquetaOrigen(origen: string): string {
  return ETIQUETAS_ORIGEN[origen] ?? origen;
}
